import { HttpErrorResponse, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { SharedPatientAssistancePharmacyPaymentStatementEarlyPaymentStatusType } from '@libs/shared/domain/patient-assistance/pharmacy/payment-statement/early-payment/shared-patient-assistance-pharmacy-payment-statement-early-payment-status.type';
import { SharedPatientAssistancePharmacyPaymentStatementReportFileNamesResponseSchema } from '@libs/shared/domain/patient-assistance/pharmacy/payment-statement/shared-patient-assistance-pharmacy-payment-statement-report-file-names-response.schema';
import { PHARMACY_PAYMENT_STATEMENT_AGGREGATED_STATUS_LABEL_MAPPER } from '@libs/shared/domain/patient-assistance/pharmacy/payment-statement/shared-patient-assistance-pharmacy-payment-statement-aggregated-status-label-mapper';
import {
  AGGREGATED_STATUS_PENDING,
  AGGREGATED_STATUS_PENDING_ADJUSTED,
  SharedPatientAssistancePharmacyPaymentStatementAggregatedStatusType,
} from '@libs/shared/domain/patient-assistance/pharmacy/payment-statement/shared-patient-assistance-pharmacy-payment-statement-aggregated-status.type';
import { SharedPatientAssistancePharmacyPaymentStatementJournalEntryTypeType } from '@libs/shared/domain/patient-assistance/pharmacy/payment-statement/shared-patient-assistance-pharmacy-payment-statement-journal-entry-type.type';
import { SharedPatientAssistancePharmacyPaymentStatementStatusType } from '@libs/shared/domain/patient-assistance/pharmacy/payment-statement/shared-patient-assistance-pharmacy-payment-statement-status.type';
import { DateService } from '@services/date.service';
import { HttpService } from '@services/http.service';
import {
  PharmacyPaymentStatementCreateRequestModel,
  PharmacyPaymentStatementModel,
  PharmacyPaymentStatementResponseModel,
  PharmacyPaymentStatementUpdateRequestModel,
} from '@services/models/pharmacy-payment-statement';
import {
  extraPaidClaimsSearchCriteria,
  extraPaymentReportColumns,
  paidClaimsSearchCriteria,
  paymentReportColumns,
} from '@services/pharmacy-payment-statements.columns';
import { DateRange } from '@shared/components/chitin/molecules/daterange-picker/date-range';
import { SelectListItem } from '@shared/models/select-list-item';
import { chain } from 'lodash';
import moment from 'moment';
import { catchError, map, Observable, of } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class PharmacyPaymentStatementsService extends HttpService {
  private readonly dateService = inject(DateService);

  public getPeriodEndLabel(periodEnd: string): string {
    return this.dateService.moveDate(periodEnd.substring(0, 10), -1, 'day').toISOString(true);
  }

  public mapStatusToBadgeClass(status: SharedPatientAssistancePharmacyPaymentStatementStatusType) {
    return {
      paid: 'bg-success',
      unpaid: 'bg-light text-black',
      closed: 'bg-dark text-white',
      pending: 'bg-info text-white',
    }[status];
  }

  public createAggregatedStatusDropdownItems(): SelectListItem[] {
    return chain(Object.entries(PHARMACY_PAYMENT_STATEMENT_AGGREGATED_STATUS_LABEL_MAPPER))
      .filter(([key]) => ![AGGREGATED_STATUS_PENDING, AGGREGATED_STATUS_PENDING_ADJUSTED].includes(key))
      .map(([key, value]) => ({ name: value, value: key }))
      .value();
  }

  public mapAggregatedStatusToBadgeClass(status?: SharedPatientAssistancePharmacyPaymentStatementAggregatedStatusType): string {
    return status
      ? {
          paid: 'bg-success',
          paidAdjusted: 'bg-success',
          unpaid: 'bg-light text-black',
          pending: 'bg-info text-white',
          unpaidAdjusted: 'bg-light text-black',
          closed: 'bg-dark text-white',
          closedAdjusted: 'bg-dark text-white',
          pendingAdjusted: 'bg-info text-white',
        }[status]
      : '';
  }

  public mapAggregatedStatusToStatusName(status?: SharedPatientAssistancePharmacyPaymentStatementAggregatedStatusType): string {
    return status ? PHARMACY_PAYMENT_STATEMENT_AGGREGATED_STATUS_LABEL_MAPPER[status] : '';
  }

  public mapRequestStatusToBadgeClass(status: SharedPatientAssistancePharmacyPaymentStatementEarlyPaymentStatusType) {
    return {
      accepted: 'bg-success',
      rejected: 'bg-danger text-black',
      requested: 'bg-warning text-black',
      unrequested: 'bg-light text-black',
    }[status];
  }

  getPaymentStatement(npi: string, periodEnd: string, isForChain: boolean): Observable<PharmacyPaymentStatementModel | undefined> {
    return this.httpClient
      .get<PharmacyPaymentStatementResponseModel>(this.createItemUrl(npi, periodEnd), {
        params: { isForChain },
      })
      .pipe(
        map(response => this.mapResponse(response)),
        catchError((error: HttpErrorResponse) => this.handleGetError(error)),
      );
  }

  getReportFileNames(npi: string, periodEnd: string, isForChain: boolean): Observable<string[]> {
    return this.httpClient
      .get<SharedPatientAssistancePharmacyPaymentStatementReportFileNamesResponseSchema>(
        `${this.createItemUrl(npi, periodEnd)}/report-file-names`,
        { params: { isForChain } },
      )
      .pipe(map(response => response.reportFileNames));
  }

  createPaymentStatement(npi: string, data: PharmacyPaymentStatementCreateRequestModel): Observable<PharmacyPaymentStatementModel> {
    return this.httpClient
      .post<PharmacyPaymentStatementResponseModel>(this.createCollectionUrl(npi), data)
      .pipe(map(response => this.mapResponse(response)));
  }

  updatePaymentStatement(
    npi: string,
    periodEnd: string,
    data: PharmacyPaymentStatementUpdateRequestModel,
  ): Observable<PharmacyPaymentStatementModel> {
    return this.httpClient
      .patch<PharmacyPaymentStatementResponseModel>(this.createItemUrl(npi, periodEnd), data)
      .pipe(map(response => this.mapResponse(response)));
  }

  resetTransmittedDate(npi: string, periodEnd: string): Observable<PharmacyPaymentStatementModel> {
    return this.httpClient
      .post<PharmacyPaymentStatementResponseModel>(`${this.createItemUrl(npi, periodEnd)}/reset-transmitted-date`, {})
      .pipe(map(response => this.mapResponse(response)));
  }

  requestEarlyPayment(npi: string, periodEnd: string, earlyPaymentProductId: string): Observable<PharmacyPaymentStatementModel> {
    return this.httpClient
      .post<PharmacyPaymentStatementResponseModel>(`${this.createItemUrl(npi, periodEnd)}/early-payment`, { earlyPaymentProductId })
      .pipe(map(response => this.mapResponse(response)));
  }

  exportPaymentStatements({
    npi,
    periodEnd,
    status,
    journalEntryTypes,
    isForChain,
  }: {
    npi: string;
    periodEnd: DateRange;
    isForChain: boolean;
    status?: SharedPatientAssistancePharmacyPaymentStatementAggregatedStatusType;
    journalEntryTypes?: SharedPatientAssistancePharmacyPaymentStatementJournalEntryTypeType[];
  }) {
    const urlParams = new HttpParams({
      fromObject: {
        periodEndFrom: moment(periodEnd.from).format('YYYY-MM-DD') + 'T05:00:00Z',
        periodEndTo: moment(periodEnd.to).add(1, 'day').format('YYYY-MM-DD') + 'T05:00:00Z',
        aggregatedStatus: status ?? '',
        journalEntryTypes: journalEntryTypes?.join(',') ?? '',
        isForChain,
      },
    });
    const url = `${this.urlService.PA_SERVICE}/pharmacy-by-npi/${npi}/payment-statements/export?${urlParams.toString()}`;
    return this.httpClient.get<{ fileUrl: string }>(url);
  }

  getExtraBenefitTotalForDateRange(createdFrom: Date, createdTo: Date, pharmacyNcpdp: string) {
    return this.httpClient.post<{ extraBenefitAmount: { count: number; sum: number } }>(
      this.urlService.AGGREGATES_URL + this.urlService.AGGREGATES_SERVICE.paidClaimsStats,
      {
        filter: {
          createdFrom,
          createdTo,
          pharmacyNcpdp,
        },
      },
    );
  }

  getPharmacyPaymentStatementReport(
    pharmacyName: string,
    periodStart: string | undefined,
    periodEnd: string | undefined,
    ncpdp: string,
    type: 'claims' | 'extra' = 'claims',
  ) {
    const today = new Date().toISOString();
    const body: Record<string, unknown> = {
      extract_name: 'pharmacy_remittance_report',
      enable_report_filename_options: 1,
      output_filename: `${pharmacyName.replace(/\W+/g, '-')}_${(periodStart ?? today).substring(0, 10)}_${this.dateService
        .moveDate(periodEnd, -1, 'day')
        .toISOString(true)
        .substring(0, 10)}.xlsx`,
      current_date: (periodEnd ?? today).substring(0, 10),
      file_by_pharmacy: 0,
      pharmacy_search_criteria: {
        ncpdp: ncpdp,
      },
      tenants: ['ALM', 'GLD', 'INCY'],
      spreadsheet_data: {
        file_type: 'xlsx',
        sheets: {
          Payments: type === 'claims' ? paymentReportColumns : extraPaymentReportColumns,
        },
      },
      paid_claims_search_criteria_list: type === 'extra' ? extraPaidClaimsSearchCriteria : paidClaimsSearchCriteria,
    };
    if (periodStart) {
      body.start_date = periodStart.substring(0, 10);
    }
    return this.httpClient.post<string[]>(`${this.urlService.DATA_DELIVERY_DOWNLOAD_URL}`, body);
  }

  calculateUpcomingPaymentFromLastStatement(periodStart: string | Date, periodEnd: string | Date) {
    const _periodStart = moment(periodStart);
    const _periodEnd = moment(periodEnd);

    const nextPeriodStart =
      _periodStart.date() > 1
        ? _periodStart.add(1, 'month').startOf('month').set('hours', 5)
        : _periodStart.set('date', 16).set('hours', 5);
    const nextPeriodEnd =
      _periodEnd.date() > 15 ? _periodEnd.add(1, 'month').set('date', 15).set('hours', 5) : _periodEnd.endOf('month').set('hours', 5);
    return {
      periodStart: nextPeriodStart.toDate(),
      periodEnd: nextPeriodEnd.toDate(),
      paymentDate: nextPeriodEnd.add(1, 'day').toDate(),
    };
  }

  calculateUpcomingPaymentFromPeriodEnd(periodEnd: string | Date) {
    const _periodEnd = moment(periodEnd);

    const nextPeriodEnd = moment(periodEnd);
    const nextPeriodStart =
      _periodEnd.date() > 1
        ? _periodEnd.subtract(1, 'month').startOf('month').set('hours', 5)
        : _periodEnd.subtract(1, 'month').set('date', 16).set('hours', 5);
    return {
      periodStart: nextPeriodStart.toDate(),
      periodEnd: nextPeriodEnd.toDate(),
      paymentDate: nextPeriodEnd.add(1, 'day').toDate(),
    };
  }

  private mapResponse({ paymentStatement, systemAdjustments, earlyPaymentProducts }: PharmacyPaymentStatementResponseModel) {
    return { ...paymentStatement, systemAdjustments, earlyPaymentProducts: earlyPaymentProducts ?? [] };
  }

  private createCollectionUrl(npi: string): string {
    return `${this.urlService.PA_SERVICE}/pharmacy-by-npi/${encodeURIComponent(npi)}/payment-statements`;
  }

  private createItemUrl(npi: string, periodEnd: string): string {
    return `${this.createCollectionUrl(npi)}/${encodeURIComponent(periodEnd)}`;
  }
}
